<template>
    <div>
        <PhotoWidget ref="PhotoWidget"></PhotoWidget>
        <GenericPopups ref="GenericPopups"></GenericPopups>
        <WeightValidation ref="WeightValidation"></WeightValidation>
        <LoadingSpinner :busy="loading"></LoadingSpinner>
        <b-card v-if="order && !loading">
            <template v-slot:header>
                <OrderCardHeader :order="order"></OrderCardHeader>
            </template>
            <b-row>
                <b-col>
                    <template v-if="!captureCRC">
                        <b-row>
                            <b-col>
                                <video-streamer-control ref="picturePreview" camera="overheadCamera" auto-record mode="video" video-type="process" :order-id="order.id"
                                                        v-on:photo-taken="savePhotoAsync($event)"></video-streamer-control>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col class="pt-1">
                                <b-button size="lg" @click="startRecording" :disabled="!_canEdit"><span class="text-danger"><i
                                    class="fa-regular fa-circle"></i></span>
                                </b-button>
                                <b-button size="lg" class="mx-2" @click="stopRecording" :disabled="!_canEdit"><i
                                    class="fa-regular fa-square"></i></b-button>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col class="pt-3">
                                <b-alert show><i class="fa-duotone fa-exclamation-triangle fa-fw"></i> Stop recording to capture Customer Return Card images.</b-alert>
                            </b-col>
                        </b-row>
                    </template>

                    <b-card header="Customer Return Card Images" class="mt-2" v-if="captureCRC">
                        <b-row class="no-gutters">
                            <b-col cols="7">
                                <video-streamer-control ref="scaleCamera" camera="scaleCamera" mode="picture"
                                                        v-on:photo-taken="savePhotoAsync"></video-streamer-control>
                            </b-col>
                            <b-col class="ml-2">
                                <div><label><i class="fa-light fa-file-check mr-1"></i>Capture Customer Return Card</label></div>
                                <b-button size="lg" variant="secondary" :class="prompt_take_picture?'next-step' :''"
                                          @click="takePhoto('crc')" :disabled="!_canEdit || !!photoLoading">
                                    <i class="fa-regular fa-spinner-third fa-spin" v-if="photoLoading == 'crc'"></i>
                                    <i class="fa-regular fa-camera" v-else></i>
                                </b-button>
                                <b-row v-if="prompt_take_picture">
                                    <b-col class="text-success my-2">
                                        <span><small>Please take a picture of the front and back of the Customer Return Card.</small></span>
                                    </b-col>
                                </b-row>

                                <div class="mt-3"><label><i class="fa-light fa-id-card mr-1"></i>Capture Additional Documents</label></div>
                                <b-card body-class="p-2">
                                    <b-form-group
                                            label="Type:"
                                            label-for="document-type"
                                            label-class="font-weight-bold"
                                    >
                                        <b-form-select id="document-type"
                                                    size="sm"
                                                    v-model="document_upload.type_selected"
                                                    :options="document_upload.type_options">
                                        </b-form-select>
                                    </b-form-group>
                                    <b-form-group
                                            label="Description:"
                                            label-for="document-type"
                                            label-class="font-weight-bold"
                                            v-if="document_upload.type_selected == 'Other'" class="mt-2">
                                        <b-input size="sm" v-model="document_upload.description">
                                        </b-input>
                                    </b-form-group>
                                    <b-button size="" variant="secondary"
                                          @click="takePhoto(document_upload.type_selected == 'Other' ? document_upload.description : document_upload.type_selected)" :disabled="!_canEdit || !!photoLoading">
                                    <i class="fa-regular fa-spinner-third fa-spin" v-if="photoLoading == 'identification'"></i>
                                    <i class="fa-regular fa-camera" v-else></i>
                                </b-button>
                                </b-card>
                            </b-col>
                        </b-row>
                        <b-form-row class="my-1">
                            <b-col cols="3" class="mb-2" v-for="(item, index) in pictures" :key="index">
                                <img style="width: 100%;" :src="createImageURL(item)" alt=""
                                     v-on:click="activateImageModal(item)">
                                <b-button size="sm" variant="link" pill class="delete-photo-icon"
                                          @click="removePhoto(index)" :disabled="!_canEdit">
                                    <i class="fa-solid fa-times-circle text-danger"></i>
                                </b-button>
                            </b-col>
                        </b-form-row>
                    </b-card>
                </b-col>


                <b-col>

                    <div class="d-flex justify-content-between pb-2">
                        <div class="text-success">
                            <span class="font-weight-bold h2" :class="weightClass()">
                                <ScaleWeight v-on:scale-stable="scaleStable = $event"
                                             v-on:scale-weight="scaleWeight = $event"
                                             ref="scale"></ScaleWeight>
                            </span>
                        </div>
                        <div>
                            <i class="fa-light fa-box mr-1"></i><label class="mb-0">Packages</label><br>
                            <span class="font-weight-bold h5">{{ totalPackages }}</span>
                        </div>
                    </div>


                    <!--*********************
                       WEIGHTS AND PACKAGES
                    **********************-->
                    <b-card header="Weights and Packages" class="header-sm">
                        <div v-for="(item, index) in packages" :key="item.id">
                            <div class="mb-4"
                                 :class="item.tracking_number.tracking_number == target_barcode ? 'next-package': ''">
                                <h6 class="text-uppercase mb-0"><i class="fa-solid fa-box mr-2"></i>Package
                                    #{{ item.tracking_number.tracking_number }}
                                </h6>
                                <b-row class="text-center">
                                    <b-col cols="4">
                                        <label>Total Weight</label>
                                        <div class="font-weight-bold" :class="{'text-success': item.total_dwt_saved}">
                                            {{ item.appraisal_total_dwt | toFixed2 }}
                                        </div>
                                        <b-button
                                            :class="item.tracking_number.tracking_number == target_barcode  && !item.appraisal_total_dwt ? 'next-step' : ''"
                                            class="mt-1" size="sm" variant="light"
                                            :disabled="!scaleStable || working || !_canEdit || item.tracking_number.tracking_number != target_barcode"
                                            @click="attemptSavePackageTotalWeight(index)">
                                            <i class="fa-light fa-weight mr-1"></i>Capture
                                        </b-button>
                                    </b-col>
                                    <b-col cols="4">
                                        <label>Inventory Weight</label>
                                        <div class="font-weight-bold" :class="{'text-success': item.items_dwt_saved}">
                                            {{ item.items_dwt | toFixed2 }}
                                        </div>
                                        <b-button
                                            :class="item.appraisal_total_dwt && !item.items_dwt && item.tracking_number.tracking_number == target_barcode ? 'next-step': ''"
                                            class="mt-1" size="sm" variant="light"
                                            :disabled="!scaleStable || working || !_canEdit || !item.appraisal_total_dwt || item.tracking_number.tracking_number != target_barcode"
                                            @click="savePackageWeightValue(index, 'items_dwt')">
                                            <i class="fa-light fa-weight mr-1"></i>Capture
                                        </b-button>
                                    </b-col>
                                    <b-col cols="4">
                                        <label>Packaging Weight</label>
                                        <div class="font-weight-bold"
                                             :class="{'text-success': item.materials_dwt_saved}">
                                            {{ item.materials_dwt | toFixed2 }}
                                        </div>
                                        <b-button
                                            :class="item.items_dwt && !item.materials_dwt && item.tracking_number.tracking_number == target_barcode ? 'next-step': ''"
                                            class="mt-1" size="sm" variant="light"
                                            :disabled="!scaleStable || working || !_canEdit || !item.items_dwt || item.tracking_number.tracking_number != target_barcode"
                                            @click="savePackageWeightValue(index, 'materials_dwt')">
                                            <i class="fa-light fa-weight mr-1"></i>Capture
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </div>
                        </div>

                        <div>
                            <h6 class="text-uppercase"><i class="fa-solid fa-boxes mr-2"></i>Totals for all packages
                            </h6>
                            <b-row class="text-center">
                                <b-col>
                                    <label>Total Weight</label>
                                    <div class="font-weight-bold">{{ (totalsAllPackages.totalWeight) }}</div>
                                </b-col>
                                <b-col>
                                    <label>Inventory Weight</label>
                                    <div class="font-weight-bold">{{ (totalsAllPackages.inventoryWeight) }}
                                    </div>
                                </b-col>
                                <b-col>
                                    <label>Packaging Weight</label>
                                    <div class="font-weight-bold">{{ (totalsAllPackages.packagingWeight) }}</div>
                                </b-col>
                                <b-col>
                                    <label for="package-condition">Packing Quality</label>
                                    <b-form-select
                                        id="packing-quality"
                                        v-model="order.packing_quality"
                                        :options="packing_quality.options"
                                        :disabled="working || !_canEdit"
                                        size="sm">
                                    </b-form-select>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>
                    <!--END WEIGHTS AND PACKAGES-->


                    <b-form-row>

                        <!-- CUSTOMER RETURN CARD -->
                        <b-col cols="3">
                            <b-card header="Customer Return Card"
                                    class="header-sm mb-2"
                                    body-class="px-3 py-1">
                                <b-form-group label-for="card-present" label-class="label"
                                              v-b-tooltip.hover
                                              :title="order.card_present ? 'yes':'no'">
                                    <template v-slot:label>
                                        <i class="fa-light fa-file-check mr-1"></i>Card Present?
                                    </template>
                                    <b-form-checkbox
                                        class="nice-toggle-success"
                                        id="card-present"
                                        v-model="order.card_present"
                                        name="check-button"
                                        :disabled="working || !_canEdit"
                                        switch>
                                    </b-form-checkbox>
                                </b-form-group>

                                <b-form-group label-for="card-signed" label-class="label"
                                              class="mb-1"
                                              v-b-tooltip.hover
                                              :title="order.card_signed ? 'yes':'no'">
                                    <template v-slot:label>
                                        <i class="fa-light fa-signature mr-1"></i>Card Signed?
                                    </template>
                                    <b-form-checkbox
                                        class="nice-toggle-success"
                                        id="card-signed"
                                        v-model="order.card_signed"
                                        name="check-button"
                                        :disabled="working || !_canEdit"
                                        switch>
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-card>

                            <b-card header="Additional Packaging"
                                    class="header-sm mb-2"
                                    body-class="px-3 py-1">
                                <b-form-group label-for="additional_packaging" label-class="label"
                                              class="mb-1"
                                              v-b-tooltip.hover
                                              :title="order.additional_packaging ? 'yes':'no'">
                                    <template v-slot:label>
                                        <i class="fa-light fa-box mr-1"></i>Additional Packaging?
                                    </template>
                                    <b-form-checkbox
                                        class="nice-toggle-success"
                                        id="additional_packaging"
                                        v-model="order.additional_packaging"
                                        name="check-button"
                                        :disabled="working || !_canEdit"
                                        switch>
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-card>
                        </b-col>

                        <!-- IDENTIFICATION -->
                        <b-col>
                            <validation-observer ref="identification_observer" v-slot="{ passes, invalid }">
                                <b-card header="Identification"
                                        class="header-sm"
                                        :header-class="''"
                                        body-class="px-3 py-2 min-card-height">
                                    <b-form-group label-for="id-type" class="mb-0" label-class="label">
                                        <template v-slot:label>
                                            <i class="fa-light fa-id-card mr-1"></i>Identification Type
                                        </template>
                                        <validation-provider
                                            name="id type"
                                            :rules="{ required: true}"
                                            immediate
                                            v-slot="validationContext">
                                            <b-form-select
                                                id="id-type"
                                                v-model="customer.identity_type"
                                                :options="identification.type.options"
                                                :disabled="working || !_canEdit"
                                                size="sm">
                                            </b-form-select>
                                        </validation-provider>
                                        <template v-if="customer.identity_type != 'military' ">
                                            <validation-provider
                                                    name="State"
                                                    :rules="{ required: true}"
                                                    immediate
                                                    v-slot="validationContext">
                                                <input type="hidden" v-model="customer.identity_state">
                                                <multiselect v-model="_state"
                                                             :options="US_STATES_LIST_UNFILTERED"
                                                             :optionHeight="8" group-values="types" group-label="name"
                                                             :searchable="true" :close-on-select="true" :show-labels="false"
                                                             :group-select="false" track-by="id" label="name"
                                                             :disabled="working || !_canEdit"
                                                             placeholder="Select State">
                                                </multiselect>
                                            </validation-provider>
                                        </template>
                                        <validation-provider
                                            name="ID Number"
                                            :rules="{ required: true}"
                                            @click="$event.target.select()"
                                            immediate
                                            v-slot="validationContext">
                                            <b-form-input type="text" v-model="customer.identity_number" size="sm" lazy
                                                          :disabled="working || !_canEdit"
                                                          placeholder="ID Number">
                                            </b-form-input>
                                        </validation-provider>
                                        <validation-provider
                                            name="ID Expiration"
                                            :rules="{ required: true, datenotexpired: true}"
                                            @click="$event.target.select()"
                                            immediate
                                            v-slot="validationContext">
                                            <b-form-input type="date" v-model="customer.identity_expiration" size="sm" lazy
                                                          :disabled="working || !_canEdit"
                                                          placeholder="ID Expiration">
                                            </b-form-input>
                                        </validation-provider>
                                        <validation-provider
                                            name="date"
                                            :rules="{ required: true, dobover18:true,dobreasonablerange:true }"
                                            immediate
                                            v-slot="validationContext">
                                            <i class="fa-light fa-calendar mr-1"></i>
                                            <label for="dob-field">
                                                DOB
                                            </label>
                                            <b-form-input type="date" v-model="dob" id="dob-field"
                                                          v-on:blur="_updatedob()" size="sm" lazy
                                                          :disabled="working || !_canEdit"
                                                          placeholder="DOB"
                                                          class="">
                                            </b-form-input>
                                        </validation-provider>
                                        <validation-provider
                                            name="SSN Last 4"
                                            :rules="{ required: false, digits:4 }"
                                            @click="$event.target.select()"
                                            immediate
                                            v-slot="validationContext">
                                            <i class="fa-light fa-square-4 mr-1"></i>
                                            <label for="dob-field">
                                                SSN Last 4
                                            </label>
                                            <b-form-input v-model="customer.ssn_last_4" size="sm"
                                                          :formatter="$inputSanitizer.toDigits"
                                                          :disabled="working || !_canEdit"
                                                          :state="getValidationState(validationContext)"
                                                          placeholder="xxxx">
                                            </b-form-input>
                                        </validation-provider>
                                    </b-form-group>
                                </b-card>
                            </validation-observer>
                        </b-col>

                        <!-- PAYMENT -->
                        <b-col>
                            <b-card header="Payment"
                                    class="header-sm"
                                    body-class="px-3 py-2  min-card-height">
                                <validation-observer ref="payment_method_observer" v-slot="{ passes, invalid, errors }">

                                    <b-form-group label-for="payment-method" label-class="label">
                                        <template v-slot:label>
                                            <i class="fa-light fa-dollar-sign mr-1"></i>Payment Method
                                        </template>
                                        <b-form-select v-model="order.payment_method" :options="payment.options"
                                                       id="payment-method"
                                                       :disabled="working || !_canEdit"
                                                       size="sm"></b-form-select>
                                    </b-form-group>

                                    <template v-if=" order.payment_method === 'paypal' ">

                                        <validation-provider
                                                :rules="{ required: true, email: true}"
                                                immediate
                                                name="paypal_email"
                                                v-slot="validationContext">
                                            <b-form-input
                                                v-model="order.paypal_email"
                                                lazy
                                                placeholder="PayPal email"
                                                size="sm"
                                                @click="$event.target.select()"
                                                type="email"
                                                :disabled="working || !_canEdit"
                                                :state="getValidationState(validationContext)"
                                                v-b-tooltip.hover.right
                                                :title="getValidationComplaint(validationContext)"
                                                class="mb-3">
                                            </b-form-input>
                                        </validation-provider>
                                    </template>

                                    <template v-else-if=" order.payment_method === 'direct-deposit' ">

                                        <validation-provider
                                                :rules="{ required: true, regex: /^\d{9}$/ }"
                                                immediate
                                                name="bank routing"
                                                v-slot="validationContext">
                                            <b-form-input
                                                v-model="order.bank_routing"
                                                maxlength="9"
                                                :formatter="$inputSanitizer.toDigits"
                                                lazy
                                                @click="$event.target.select()"
                                                placeholder="Routing Number"
                                                size="sm"
                                                :disabled="working || !_canEdit" :state="getValidationState(validationContext)"
                                                v-b-tooltip.hover.right
                                                :title="getValidationComplaint(validationContext)"
                                                class="mt-2">
                                            </b-form-input>
                                        </validation-provider>
                                        <validation-provider
                                                :rules="{ required: true}"
                                                immediate
                                                name="bank account"
                                                v-slot="validationContext">
                                            <b-form-input
                                                v-model="order.bank_account"
                                                :formatter="$inputSanitizer.toDigits"
                                                lazy
                                                @click="$event.target.select()"
                                                placeholder="Account Number"
                                                size="sm"
                                                :disabled="working || !_canEdit"
                                                :state="getValidationState(validationContext)"
                                                v-b-tooltip.hover.right
                                                :title="getValidationComplaint(validationContext)"
                                                class="my-2">
                                            </b-form-input>
                                        </validation-provider>
                                    </template>


                                    <b-form-group label-for="bonus-card" class="mb-0" label-class="label">
                                        <template v-slot:label>
                                            <i class="fa-light fa-badge-dollar mr-1"></i>5% bonus
                                        </template>
                                        <b-form-checkbox
                                            id="bonus-card"
                                            v-model="order.bonus_card_present"
                                            name="check-button"
                                            :disabled="working || !_canEdit || !_canEditBonus"
                                            class="nice-toggle-success"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                    <b-form-group label-for="sequence-bonus" class="mb-0" label-class="label">
                                        <template v-slot:label>
                                            <i class="fa-light fa-badge-dollar mr-1"></i>10% bonus
                                        </template>
                                        <b-form-checkbox
                                            id="sequence-bonus"
                                            v-model="order.trust_sequence_bonus"
                                            name="check-button"
                                            :disabled="working || !_canEdit || trust_sequence_bonus_preset"
                                            class="nice-toggle-success"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </validation-observer>
                            </b-card>
                        </b-col>
                    </b-form-row>
                    <!--END CRC/IDENTIFICATION/PAYMENT-->


                    <b-button variant="primary" size="lg" class="float-right shadow"
                              :disabled="working || !_canEdit || !canProceed || finalSave"
                              @click="finishProcessing"
                    >
                        <i v-if="finalSave" class="fa-regular fa-spin fa-spinner mr-2"></i><i v-else
                                                                                      class="fa-solid fa-arrow-circle-right mr-2"></i>Start
                        Inventory Appraisal
                    </b-button>
                </b-col>
            </b-row>

            <template v-if="this.$parent.state === 'appraisals-appraisal'">
                <!--**************************
                           START INVENTORY
                   ***************************-->
                <b-row>

                    <b-col>
                        <img class="img-fluid" src="https://via.placeholder.com/600x400" alt="">
                        <div class="d-flex my-2 align-items-center">
                            <b-button class="text-white"><i class="fa-regular fa-video"></i></b-button>
                            <b-button variant="success" class="mx-3 font-weight-bold">
                                <i class="fa-regular fa-image"></i> + <i class="fa-regular fa-balance-scale"></i> Capture
                            </b-button>
                            <b-button class="text-white"><i class="fa-regular fa-image"> +</i></b-button>
                            <span class="font-weight-bold h3 text-success ml-3 mb-0"><i
                                class="fa-duotone fa-weight mr-2"></i>7.00</span>
                        </div>
                        <b-form-row class="my-3">
                            <b-col>
                                <img style="width: 100%;" src="https://via.placeholder.com/200x150" alt="">
                            </b-col>

                            <b-col>
                                <img style="width: 100%;" src="https://via.placeholder.com/200x150" alt="">
                            </b-col>

                            <b-col>
                                <img style="width: 100%;" src="https://via.placeholder.com/200x150" alt="">
                            </b-col>
                        </b-form-row>


                        <b-table-simple outlined bordered small class="mt-4">
                            <b-thead>
                                <b-tr class="bg-light">
                                    <th colspan="3"><span class="h5 py-3">Item Summary</span></th>
                                </b-tr>
                                <b-tr>
                                    <b-th>Element</b-th>
                                    <b-th>Weight</b-th>
                                    <b-th>EAV</b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr>
                                    <b-td><b>Total</b></b-td>
                                    <b-td>
                                        <b-input-group size="sm" append="dwt">
                                            <b-form-input type="number"></b-form-input>
                                        </b-input-group>
                                    </b-td>
                                    <b-td>
                                        <b-input-group size="sm" prepend="$">
                                            <b-form-input type="number"></b-form-input>
                                        </b-input-group>
                                    </b-td>
                                </b-tr>
                                <b-tr>
                                    <b-td>(Parent Metal)</b-td>
                                    <b-td>0.00 dwt</b-td>
                                    <b-td>0.00</b-td>
                                </b-tr>

                            </b-tbody>
                        </b-table-simple>
                    </b-col>

                    <b-col cols="7">
                        <b-card header="Item Description" header-class="h5" body-class="px-3 py-4">
                            <b-form-row class="mb-3">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Quick Selection:</label>
                                </b-col>
                                <b-col>
                                    <div class="d-flex justify-content-between">
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Filling</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Necklace</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Earring</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Other</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Bracelet/Anklet</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Pendant</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Charm</small>
                                        </b-button>
                                        <b-button size="sm" v-model="itemDescription.item.type">
                                            <small>Coin</small>
                                        </b-button>
                                    </div>
                                </b-col>

                            </b-form-row>

                            <!--TYPE-->
                            <b-form-row class="mb-3">
                                <b-col cols="2" class="text-right">
                                    <label for="input-type" size="sm" class="font-weight-bold">Type:</label>
                                </b-col>
                                <b-col cols="10">
                                    <b-form-select id="input-type" v-model="selected"
                                                   :options="options"></b-form-select>
                                </b-col>
                            </b-form-row>


                            <!--METAL-->
                            <b-form-row class="mb-3">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Metal</label>
                                </b-col>

                                <b-col>
                                    <b-form-radio-group label="Individual radios">
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="gold">Gold
                                        </b-form-radio>
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="silver">Silver
                                        </b-form-radio>
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="platinum">
                                            Platinum
                                        </b-form-radio>
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="palladium">
                                            Palladium
                                        </b-form-radio>
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="gold_filled">Gold
                                            Filled
                                        </b-form-radio>
                                        <b-form-radio size="sm" v-model="itemDescription.item.metal"
                                                      name="some-radios"
                                                      value="other">Other
                                        </b-form-radio>
                                    </b-form-radio-group>
                                </b-col>
                            </b-form-row>


                            <!--PURITY & QUANTITY-->
                            <b-form-row class="mb-3">

                                <b-col cols="2" class="text-right">
                                    <label for="input-purity"
                                           size="sm"
                                           class="font-weight-bold">Purity:</label>
                                </b-col>
                                <b-col>
                                    <b-form-select
                                        id="input-purity"
                                        v-model="itemDescription.item.purity"
                                        :options="itemDescription.options.goldPurities"
                                    >
                                    </b-form-select>
                                </b-col>

                                <b-col cols="2" class="text-right">
                                    <label for="input-quantity" size="sm" class="font-weight-bold">Quantity:</label>
                                </b-col>
                                <b-col>
                                    <b-form-input id="input-quantity"
                                                  @focus="console.log($event); $event.target.select()"
                                                  v-model="itemDescription.item.quantity"
                                                  type="number"></b-form-input>
                                </b-col>

                            </b-form-row>


                            <!--COLOR & BRAND-->
                            <b-form-row class="mb-3">

                                <b-col cols="2" class="text-right">
                                    <label for="input-color" size="sm" class="font-weight-bold">Color:</label>
                                </b-col>
                                <b-col>
                                    <b-form-select id="input-color"
                                                   v-model="itemDescription.item.color"
                                                   :options="itemDescription.options.colors"
                                    ></b-form-select>
                                </b-col>

                                <b-col cols="2" class="text-right">
                                    <label for="input-brand"
                                           size="sm"
                                           class="font-weight-bold">Brand:</label>
                                </b-col>
                                <b-col>
                                    <b-form-select
                                        id="input-brand"
                                        v-model="itemDescription.item.brand"
                                        :options="itemDescription.options.brands"
                                    >
                                    </b-form-select>
                                </b-col>

                            </b-form-row>


                            <!--STYLE & CONDITION-->
                            <b-form-row class="mb-3">

                                <b-col cols="2" class="text-right">
                                    <label for="input-style" size="sm" class="font-weight-bold">Style:</label>
                                </b-col>
                                <b-col>
                                    <b-form-select id="input-style"
                                                   v-model="itemDescription.item.style"
                                                   :options="itemDescription.options.styles"
                                    ></b-form-select>
                                </b-col>

                                <b-col cols="2" class="text-right">
                                    <label for="input-condition" size="sm"
                                           class="font-weight-bold">Condition:</label>
                                </b-col>
                                <b-col>
                                    <b-form-select id="input-condition"


                                                   v-model="itemDescription.item.condition"
                                                   :options="itemDescription.options.conditions"
                                    ></b-form-select>
                                </b-col>

                            </b-form-row>


                            <!--ENGRAVINGS & SERIAL NUMBERS-->
                            <b-form-row class="mb-3">

                                <b-col cols="2" class="text-right">
                                    <label for="input-engravings" size="sm" class="font-weight-bold">Engravings/<br>Serial:</label>
                                </b-col>
                                <b-col cols="4">
                                    <b-form-select id="input-engravings"
                                                   v-model="selected"
                                                   :options="options">

                                    </b-form-select>
                                </b-col>

                            </b-form-row>


                            <!--TEST METHODS-->
                            <b-form-row class="my-3">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Test Method:</label>
                                </b-col>


                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-xrf"
                                                  label-class="font-weight-bold mb-1">
                                        <template v-slot:label>
                                            <i class="text-warning mr-1 fa-solid fa-bolt"></i>XRF
                                        </template>
                                        <b-form-checkbox
                                            id="test-xrf"
                                            v-model="itemDescription.item.test_methods.xrf"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-acid"
                                                  label-class="font-weight-bold mb-1">
                                        <template v-slot:label>
                                            <i class="text-success mr-1 fa-solid fa-flask"></i>Acid
                                        </template>
                                        <b-form-checkbox
                                            id="test-acid"
                                            v-model="itemDescription.item.test_methods.acid"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-magnet"
                                                  label-class="font-weight-bold mb-1">
                                        <template v-slot:label>
                                            <i class="text-danger mr-1 fa-solid fa-magnet"></i>Magnet
                                        </template>
                                        <b-form-checkbox
                                            id="test-magnet"
                                            v-model="itemDescription.item.test_methods.magnet"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-hallmark"
                                                  label-class="font-weight-bold mb-1">
                                        <template v-slot:label>
                                            <i class="text-primary mr-1 fa-solid fa-h-square"></i>Hallmark
                                        </template>
                                        <b-form-checkbox
                                            id="test-hallmark"
                                            v-model="itemDescription.item.test_methods.hallmark"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-other"
                                                  label-class="font-weight-bold mb-1">
                                        <template v-slot:label>
                                            <i class="mr-1 fa-solid fa-map-marker-question"></i>Other
                                        </template>
                                        <b-form-checkbox
                                            id="test-other"
                                            v-model="itemDescription.item.test_methods.other"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <!--MELT HOLD-->
                            <b-form-row class="mb-2">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Melt/Hold:</label>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group>
                                        <b-form-radio v-model="itemDescription.item.melt_hold"
                                                      name="some-radios" value="melt" class="font-weight-bold">Melt
                                        </b-form-radio>
                                        <b-form-radio v-model="itemDescription.item.melt_hold"
                                                      name="some-radios" value="hold" class="font-weight-bold">Hold
                                        </b-form-radio>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="4">
                                    <b-form-group
                                        label-size="sm"
                                        label-class="font-weight-bold"
                                        label-for="additional-packaging"
                                        label="Additional Packaging:"
                                        label-align-lg="right"
                                        label-cols-lg="6">
                                        <b-form-checkbox
                                            id="additional-packaging"
                                            v-model="itemDescription.item.additional_packaging"
                                            name="check-button"
                                            class="mt-2"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group
                                        label-size="sm"
                                        label-class="font-weight-bold"
                                        label-align="right"
                                        label="EAV:"
                                        label-cols="3">
                                        <b-form-input v-model="itemDescription.item.eav"
                                                      type="number"></b-form-input>
                                    </b-form-group>

                                </b-col>

                            </b-form-row>
                        </b-card>


                        <template>
                            <div role="tablist">

                                <!-- ADDITIONS -->
                                <b-card no-body class="sub-add-accordion mb-2">
                                    <b-card-header header-tag="header" class="p-0" role="tab">
                                        <b-button variant="light" block href="#" v-b-toggle.additions
                                                  class="text-left">
                                            <span class="h5">Additions</span>
                                        </b-button>
                                    </b-card-header>
                                    <b-collapse id="additions" accordion="my-accordion" role="tabpanel">
                                        <b-card-body>
                                            <b-card-text>Additions</b-card-text>
                                        </b-card-body>
                                    </b-collapse>
                                </b-card>

                                <!-- SUBTRACTIONS -->
                                <b-card no-body class="sub-add-accordion">
                                    <b-card-header header-tag="header" class="p-0" role="tab">
                                        <b-button block variant="light" href="#" v-b-toggle.subtractions
                                                  class="text-left">
                                            <span class="h5">Subtractions</span>
                                        </b-button>
                                    </b-card-header>
                                    <b-collapse id="subtractions" accordion="my-accordion" role="tabpanel">
                                        <b-card-body>
                                            <b-card-text>Subtractions</b-card-text>
                                        </b-card-body>
                                    </b-collapse>
                                </b-card>
                            </div>
                        </template>


                        <!-- DETAILS -->
                        <b-card class="my-3" header-class="h5" header="Details">
                            <b-row>
                                <b-col cols="4">
                                    <b-form-group
                                        label-size="sm"
                                        label-class="font-weight-bold mb-0"
                                        label-for="appraiser-certificate"
                                    >
                                        <template v-slot:label>
                                            Certificate/<br>Appraisal:
                                        </template>
                                        <b-form-checkbox
                                            id="appraiser-certificate"
                                            v-model="checked"
                                            name="check-button"
                                            class="mt-0"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group
                                        label-class="font-weight-bold"
                                        id="fieldset-horizontal"
                                        label-cols-md="3"
                                        label-for="appraiser-notes"
                                        label-size="sm"
                                        label-align="right"
                                        label="Appraiser Notes:"

                                    >
                                        <b-form-textarea
                                            id="appraiser-notes"
                                            size="md"
                                            rows="3"
                                        ></b-form-textarea>
                                    </b-form-group>

                                </b-col>
                            </b-row>
                        </b-card>

                        <!-- ACTION BUTTONS -->
                        <b-form-row>
                            <b-col>
                                <b-button size="lg" block @click="cancelItem" class="text-white font-weight-bold">
                                    <i class="fa-regular fa-times-circle mr-1"></i>Cancel
                                </b-button>
                            </b-col>
                            <b-col>
                                <b-button size="lg" block class="text-white font-weight-bold">
                                    <i class="text-white fa-regular fa-check-circle mr-1"></i>Save
                                </b-button>
                            </b-col>
                            <b-col cols="6">
                                <b-button size="lg" block variant="success" @click="saveItem"
                                          class="font-weight-bold shadow">
                                    <i class="fa-regular fa-plus-circle mr-1"></i>Save and Add New
                                </b-button>
                            </b-col>
                        </b-form-row>
                    </b-col>
                </b-row>
                <!--end START INVENTORY-->
            </template>


        </b-card>
        <NotesPopup :order_id="!order ? null : order.id" :customer_id="!customer ? null : customer.id"
                    style="z-index: 1000">
        </NotesPopup>
    </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner';
import VideoStreamerControl from '@/plugins/video-streamer/components/video-streamer-control';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import NotesPopup from '../../components/NotesPopup';
import PhotoWidget from '../../components/PhotoWidget';
import GenericPopups from '../../components/GenericPopups';
import WeightValidation from '../../components/WeightValidation';
import OrderCardHeader from '@/components/OrderCardHeader';
import Multiselect from 'vue-multiselect';
import ScaleWeight from '@/components/ScaleWeight';
import _ from 'lodash';

export default {
    name: 'appraisals-process',
    components: {
        // DateRangePicker,
        VideoStreamerControl,
        LoadingSpinner,
        NotesPopup,
        OrderCardHeader,
        Multiselect,
        PhotoWidget: PhotoWidget,
        GenericPopups: GenericPopups,
        WeightValidation: WeightValidation,
        ScaleWeight,
    },
    data() {
        return {
            finalSave: false,
            prompt_take_picture: false,
            target_barcode: null,
            dob: '',
            loading: false,
            working: false,
            canProceed: false,
            scaleStable: false,
            scaleWeight: null,
            scaleSaving: false,
            scaleSaved: false,
            scaleVarianceError: false,
            captureCRC: false,
            photoLoading: false,
            number: 3,
            customer: null,
            order: null,
            trust_sequence_bonus_preset: false,
            pictures: [],
            capturingImage: false,
            identification: {
                type: {
                    selected: 'drivers-license',
                    options: [
                        {value: 'drivers-license', text: 'Drivers License'},
                        {value: 'state-issued', text: 'State ID'},
                        {value: 'passport', text: 'Passport (US only)'},
                        {value: 'military', text: 'Military ID'},
                    ]
                },
                state: {
                    selected: null,
                    options: this.US_STATES
                },
                number: null,
            },
            payment: {
                selected: null,
                options: [
                    {value: null, text: 'Payment Method', disabled: true},
                    {value: 'company-check', text: 'Company Check'},
                    {value: 'paypal', text: 'PayPal'},
                    {value: 'direct-deposit', text: 'Direct Deposit'},
                ],
            },
            packages: null,
            invalidWeightPackages: [],
            packing_quality: {
                selected: 'average',
                options: [
                    {value: null, text: 'Options', disabled: true},
                    {value: 'poor', text: 'Poor'},
                    {value: 'average', text: 'Average'},
                    {value: 'excellent', text: 'Excellent'},
                ]
            },
            document_upload: {
                type_selected: 'Identification',
                description: '',
                type_options: [
                    {value: null, text: 'Select a document type', disabled: true},
                    {value: 'Appraisal', text: 'Appraisal'},
                    {value: 'Certification', text: 'Certification'},
                    {value: 'Identification', text: 'Identification'},
                    {value: 'Identification Back', text: 'Identification Back'},
                    {value: 'Shipping Receipt', text: 'Shipping Receipt'},
                    {value: 'Other', text: 'Other'},
                ]
            },
        };
    },
    created() {
        //this.getData();

        // TODO: Remove scale code if using Scale plugin
        // let vm = this;
        // vm.$qz.connect().then(() => {
        //     vm.$scale.connect((weight, settled) => {
        //         this.scaleStable = settled;
        //         this.scaleWeight = weight;
        //     });
        // });
    },
    computed: {
        _state: {
            get() {
                let search = _.filter(this.US_STATES_LIST_UNFILTERED[0].types, (e) => {
                    return this.customer.identity_state == e.id;
                });
                if (search && search[0]) {
                    return search[0];
                }
                return null;
            },
            set(value) {
                if (value) {
                    this.customer.identity_state = value.id;
                } else {
                    this.customer.identity_state = null;
                }
            }
        },
        totalPackages: function () {
            return this.packages.length;
        },
        totalsAllPackages: function () {
            let totalWeight = 0;
            let packagingWeight = 0;
            let inventoryWeight = 0;
            this.packages.forEach(function (item) {
                totalWeight += item.appraisal_total_dwt;
                inventoryWeight += item.items_dwt;
                packagingWeight += item.materials_dwt;
            });
            return {
                totalWeight: this.numberFormat(totalWeight),
                inventoryWeight: this.numberFormat(inventoryWeight),
                packagingWeight: this.numberFormat(packagingWeight),
            };
        },
        totalAppraisedItems: function () {
            return this.total_appraised.items.length;
        },
        _canEdit() {
            return !(this.order.status_id === 8
                || this.order.status_id === 9
                || this.order.status_id === 10
                || this.order.status_id === 11
                || this.order.status_id === 12
                || this.order.status_id === 13
                || this.order.status_id === 14
                || this.order.status_id === 15);
        },
        _canEditBonus() {
            return !(_.filter(this.flags,function(e){
                    return e.flag_type.id == 14 && e.cleared_at == null;
            }).length > 0 || parseFloat(this.order.offer_quote) > 0);
        },
        _cardImagesPresent() {
            let cardImages = _.filter(this.pictures, function(o) { return ((o.type && o.type == 'crc') || (o.sub_type && o.sub_type == 'card')); });
            return cardImages.length >= 2;
        },
    },

    methods: {
        getData() {
            this.loading = true;
            this.working = true;
            this.scaleStable = false;
            this.scaleWeight = null;
            this.scaleSaving = false;
            this.scaleSaved = false;
            this.order = null;
            this.packages = null;
            this.$http.get('/orders/' + this.$route.params.id + '?include=packages,packages.trackingNumber,trackingNumbers,media')
                .then(response => {
                    console.log(response);
                    let data = response.data.data;
                    this.customer = {
                        id: data.customer.id,
                        identity_type: data.customer.identity_type,
                        identity_number: data.customer.identity_number,
                        identity_state: data.customer.identity_state,
                        identity_country: data.customer.identity_country,
                        identity_expiration: data.customer.identity_expiration,
                        dob: data.customer.dob,
                        ssn_last_4: data.customer.ssn_last_4,
                    };
                    this.order = data;
                    this.$store.commit('setActiveOrder', this.order);

                    // Set additional packaging value
                    if (!this.order.additional_packaging) {
                        this.$set(this.order, 'additional_packaging', false);
                    }

                    // Set 5% bonus on if 10% is not set
                    if (!this.order.trust_sequence_bonus) {
                        this.order.bonus_card_present = true;
                    }

                    // Disable 10% bonus toggle if 10% is already set
                    if (this.order.trust_sequence_bonus) {
                        this.trust_sequence_bonus_preset = true;
                    }

                    this.order.packages_received = data.packages ? data.packages.length : 0;
                    this.order.packages_expected = data.trackingNumbers ? _.filter(data.trackingNumbers, (e) => {
                        return e['delivered_at'] != null;
                    }).length : 0;

                    this.pictures = _.remove(data.media, (item) => {return item.type == 'photo';});
                    this.dob = data.customer.dob;
                    this.packages = data.packages;
                    this.packages.forEach((item) => {
                        item.total_dwt_saved = false;
                        item.items_dwt_saved = false;
                        item.materials_dwt_saved = false;
                    });
                    if (this.packages.length == 0 || (this.order.packages_expected - this.order.packages_received) > 0) {
                        this.$parent.$bvModal.msgBoxOk('There are missing packages for this order.', {
                            title: 'Invalid Status',
                            size: 'md',
                            buttonSize: 'sm',
                            okVariant: 'danger',
                            headerClass: 'p-2 border-bottom-0 text-center d-block',
                            footerClass: 'p-2 border-top-0',
                        });
                        this.$router.push({path: '/appraisals/'});
                    }
                    else if (this.order.customer.stop_workflow_active) {

                        this.$parent.$bvModal.msgBoxOk('Stop workflow is active on this customer.', {
                            title: 'Invalid Status',
                            size: 'md',
                            buttonSize: 'sm',
                            okVariant: 'danger',
                            headerClass: 'p-2 border-bottom-0 text-center d-block',
                            footerClass: 'p-2 border-top-0',
                        });
                        this.$router.push({path: '/appraisals/'});
                    }
                    //is after received
                    else if (this.order.status_id >= 5) { //is after kit_received
                        let goToAppraisal = true;
                        _.forEach(data.packages, (pack) => {
                            //any package is not proper set
                            if (pack.items_dwt == null) {
                                goToAppraisal = false;
                            }
                        });
                        if (goToAppraisal) {
                            this.$router.push('/appraisals/appraise/' + this.order.id);
                        }
                    }
                    // skip first change in data
                    this.$nextTick(() => {
                        if (this.$route.query.scan) {
                            this.target_barcode = this.$route.query.scan;
                        }
                        this.loading = false;
                        this.working = false;
                    });

                })
                .catch(() => {
                    this.$router.replace({path: '/appraisals/not-found/' + this.$route.params.id});
                });
        },
        logAndPass(loggable) {
            console.log(loggable);
            return loggable;
        },
        _updatedob() {
            this.customer.dob = this.dob;
        },
        finishProcessing: function () {
            /*
            ID Number: Array(1)
            State: Array(0)
            date: Array(1)
            id type: Array(0)
             */
            var processing_requirements = [];/*.concat(this.$refs.identification_observer.errors['ID Number'],
                    this.$refs.identification_observer.errors['State'],
                    this.$refs.identification_observer.errors['date'],
                    this.$refs.identification_observer.errors['id type']);*/
            var result = '';
            if (!this._cardImagesPresent && this.order.card_present) {
                result += '<li>Less than two images of the card have been taken</li>';
            }
            _.forEach(processing_requirements, (e) => {
                result += '<li>' + e + '</li>';
            });
            if (result) {
                result = '<ul>' + result + '</ul>';
                var _this = this;
                this.$refs.GenericPopups.displayPermission =
                    {
                        title: 'Issue in saving, missing fields or data',
                        permission: 'manage inventory',
                        text: result,
                        ok: 'Manager Override', //manager override will do function
                        cancel: 'Cancel', //cancel is a 'ok'
                        okAction: function () {
                            setTimeout(() => {
                                _this.checkWeightsFinished();
                            }, 200);
                        },
                        endPointsLater: ['/orders/' + this.order.id]
                    };
            } else {
                this.checkWeightsFinished();
            }
        },
        checkWeightsFinished: function () {
            let _this = this;
            let pass = true;
            for (let index = 0; index < this.packages.length; index++) {
                let errorVariant = Math.min(1, Math.max(.3 * _.round(this.packages[index].total_dwt / 20), .3)) + .001; //to handle javascript floating point error.
                console.log(Math.abs(this.packages[index].materials_dwt + this.packages[index].items_dwt - this.packages[index].total_dwt), errorVariant);
                if ((Math.abs(this.packages[index].materials_dwt + this.packages[index].items_dwt - this.packages[index].total_dwt) > errorVariant) && this.invalidWeightPackages[index] != 1) {
                    this.$refs.GenericPopups.displayPermission =
                        {
                            title: 'There is an issue with your package weight',
                            permission: 'manage inventory',
                            text: 'Your inventory weight and the weight of your packaging materials do not meet the total weight requirements of the package. Please reweigh your total package, inventory items or packaging materials to proceed with the package with tracking number #' + this.packages[index].tracking_number.tracking_number,
                            ok: 'Manager Override', //manager override will do function
                            cancel: 'Cancel', //cancel is a 'ok'
                            okAction: function () {
                                _this.invalidWeightPackages[index] = 1;
                                setTimeout(() => {
                                    _this.checkWeightsFinished();
                                }, 200);
                            },
                            endPointsLater: ['/orders/' + this.order.id]
                        };
                    pass = false;
                }
            }
            if (pass) {
                this.finishProcessingApproved();
            }
        },
        finishProcessingApproved: function () {
            var saveCount = 0;
            for (let i = 0; i < this.pictures.length; i++) {
                let picture = this.pictures[i];
                let pictureData = new FormData();
                pictureData.append('savecount', i);
                pictureData.append('pictureType', picture.type);
                if (picture.data instanceof Blob) {
                    //pictureData.append('picture', picture);
                    let reader = new FileReader();
                    reader.onload = () => {
                        pictureData.append('picture', reader.result);
                        this.$http.post('/orders/' + this.order.id + '/savepicture', pictureData)
                            .then(() => {
                                saveCount++;
                                if (saveCount == this.pictures.length) {
                                    this.finalizeFinish();
                                }
                            });
                    };
                    reader.readAsDataURL(picture.data);
                } else {
                    saveCount++;
                    if (saveCount == this.pictures.length) {
                        this.finalizeFinish();
                    }
                }
            }
            if (this.pictures.length == 0) {
                this.finalizeFinish();
            }
        },
        finalizeFinish() {
            this.finalSave = true;
            if (this.order.status_id <= 4) {
                this.order.status_id = 5;
            }
            if(this.order.payment_method !== 'direct-deposit') {
                delete this.order.bank_routing;
                delete this.order.bank_account;
            }
            if(this.order.payment_method !== 'paypal') {
                delete this.order.paypal_email;
            }
            this.$http.put('/orders/' + this.order.id, this.order).then(() => {
                this.$http.put('/customers/' + this.order.customer.id, this.customer).then(() => {
                    this.finalSave = false;
                    this.$router.push('/appraisals/appraise/' + this.$route.params.id);
                }).catch(() => {
                    this.finalSave = false;
                });
            }).catch(() => {
                this.finalSave = false;
            });
        },
        async takePhoto(type) {
            this.photoLoading = type;
            await this.$refs.scaleCamera.takePhoto(type);
        },
        convertImg(blob, type) {
            const width = 2048;
            const height = 1536;

            let canvas = document.createElement('canvas');  // Dynamically Create a Canvas Element
            canvas.width = width;  // Set the width of the Canvas
            canvas.height = height;  // Set the height of the Canvas
            let ctx = canvas.getContext('2d');  // Get the "context" of the canvas
            let img = document.createElement('img');
            img.onload = () => {
                ctx.drawImage(img, 0, 0, width, height);
                return canvas.toBlob((blob) => {
                    this.savePhoto(blob, type);
                }, 'image/jpeg', 0.75);
            };
            img.src = this.createImageURL(blob);
        },
        savePhotoAsync(blob, type) {
            this.convertImg(blob, type);
        },
        savePhoto(blob, type) {
            this.pictures.push({'data': blob, 'type': type});
            if (this._cardImagesPresent) {
                this.prompt_take_picture = false;
            }
            this.photoLoading = false;
        },

        createImageURL(item) {
            if (item.data && item.data instanceof Blob) {
                return URL.createObjectURL(item.data);
            } else if (item instanceof Blob) {
                return URL.createObjectURL(item);
            }
            return item.signed_url;
        },
        activateImageModal(item) {
            let img = null;
            if (item.data && item.data instanceof Blob) {
                img = this.createImageURL(item.data);
            } else {
                img = this.createImageURL(item);
            }
            this.$refs.PhotoWidget.renderImage(img);
        },

        removePhoto(index) {
            if (!(this.pictures[index].data instanceof Blob)) {
                this.$http.delete('/media/' + this.pictures[index].id);
            }
            this.pictures.splice(index, 1);
        },

        startRecording() {
            this.$refs.picturePreview.startRecording();
        },

        stopRecording() {
            this.$refs.picturePreview.stopRecording();
            this.captureCRC = true;
        },

        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        getValidationComplaint({errors = []}) {
            if (errors.length > 0) {
                return errors[0];
            }
            return '';
        },
        // weightDisplay() {
        //     if (!this.$qz.connected) {
        //         return 'ERR';
        //     }
        //
        //     return this.scaleWeight.toFixed(1);
        // },
        weightClass() {
            return [
                {'text-danger': this.scaleWeight === 0 && !this.scaleSaved},
                {'text-warning': !this.scaleStable},
                {'text-success': this.scaleStable && this.scaleWeight > 0},
            ];
        },
        savePackageTotalWeight(index) {
            let field = 'total_dwt';
            this.working = true;
            this.packages[index][field + '_saved'] = false;
            this.$forceUpdate();
            this.packages[index][field] = this.scaleWeight;
            this.packages[index]['appraisal_total_dwt'] = this.scaleWeight;
            let data = {};
            data[field] = this.packages[index][field];
            data['appraisal_total_dwt'] = this.packages[index]['appraisal_total_dwt'];
            this.$http.put('/packages/' + this.packages[index].id, data).then(() => {
                this.packages[index][field + '_saved'] = true;
                this.$forceUpdate();
                this.working = false;
            });
        },
        attemptSavePackageTotalWeight(index) {
            this.$refs.WeightValidation.validateIntakeWeight(this.packages[index], this.scaleWeight, () => {
                this.savePackageTotalWeight(index);
            });
        },
        savePackageWeightValue(index, field) {
            if (this.packages[index].total_dwt < this.scaleWeight) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an issue with your package weight',
                        text: 'Your ' + (field == 'items_dwt' ? 'Items' : 'Packaging') + ' Weight exceed the total weight of the package. Please reweigh your total package, inventory items or packaging materials to proceed.'
                    };
            } else {
                let _this = this;
                this.working = true;
                this.packages[index][field + '_saved'] = false;
                this.$forceUpdate();
                this.packages[index][field] = this.scaleWeight;
                let data = {};
                data[field] = this.packages[index][field];
                this.$http.put('/packages/' + this.packages[index].id, data).then(() => {
                    this.packages[index][field + '_saved'] = true;
                    this.$forceUpdate();
                    this.working = false;
                    this.invalidWeightPackages[index] = 0; //remove on retake of weight
                    if (this.packages[index].materials_dwt && this.packages[index].items_dwt) {
                        let errorVariant = Math.min(1, Math.max(.3 * _.round(this.packages[index].total_dwt / 20), .3)) + .001; //.001 is to handle javascript errors
                        console.log(Math.abs(this.packages[index].materials_dwt + this.packages[index].items_dwt - this.packages[index].total_dwt), errorVariant);
                        if ((Math.abs(this.packages[index].materials_dwt + this.packages[index].items_dwt - this.packages[index].total_dwt) > errorVariant)) {
                            this.invalidWeightPackages[index] = 0; //remove on retake of weight
                            this.$refs.GenericPopups.displayPermission =
                                {
                                    title: 'There is an issue with your package weight',
                                    permission: 'manage inventory',
                                    text: 'Your inventory weight and the weight of your packaging materials do not meet the total weight requirements of the package. Please reweigh your total package, inventory items or packaging materials to proceed.',
                                    ok: 'Manager Override', //manager override will do function
                                    cancel: 'Ok', //cancel is a 'ok'
                                    okAction: function () {
                                        _this.invalidWeightPackages[index] = 1;
                                    },
                                    endPointsLater: ['/orders/' + this.order.id]
                                };
                        }
                    }
                });
            }
        },

        // temporary methods
        numberFormat(num) {
            return (num).toFixed(2);
        },

        holdItemRowClass(item) {
            if (item.melt_hold === 'hold') {
                return 'table-warning';
            }
        },

        cancelItem() {
            this.itemDescription.item.brand = 'unknown';
            this.itemDescription.item.color = null;
            this.itemDescription.item.condition = null;
            this.itemDescription.item.metal = null;
            this.itemDescription.item.purity = null;
            this.itemDescription.item.quantity = 1;
            this.itemDescription.item.style = null;
            this.itemDescription.item.type = null;
        },

        saveItem() {

        },
        // Camera and video
        async getMedia() {
            navigator.mediaDevices.getUserMedia({video: true})
                .then((stream) => {
                    let videoWindow = document.getElementById('video_window');
                    videoWindow.srcObject = stream;
                    videoWindow.onloadedmetadata = function () {
                        videoWindow.play();
                    };
                });
        },
    },

    watch: {
        '_cardImagesPresent': function(newValue) {
            if (newValue == false && this.order.card_present) {
                this.prompt_take_picture = true;
            } else if (newValue == true && this.order.card_present) {
                this.prompt_take_picture = false;
            }
        },
        customer: {
            handler: function () {
                if (this.customer.identity_type == null) {
                    this.customer.identity_type = this.identification.type.selected;
                }
                if (!this.loading) {
                    /*this.working = true;
                    this.$http.put('/customers/' + this.order.customer.id, newValue).then(() => {
                        this.working = false;
                    }).catch(error => {
                        alert(error.response.data.message);
                        this.working = false;
                    });*/
                }
            },
            deep: true
        },
        order: {
            handler: function (newValue) {
                if (newValue.card_present && !this._cardImagesPresent && !this.loading) {
                    this.prompt_take_picture = true;
                }
                /*if (!this.loading) {
                    this.working = true;
                    this.$http.put('/orders/' + this.order.id, newValue).then(() => {
                        this.working = false;
                    }).catch(error => {
                        alert(error.response.data.message);
                        this.working = false;
                    });
                }*/
            },
            deep: true
        },
        packages: {
            handler: function () {
                this.canProceed = true;
                _.forEach(this.packages, (item) => {
                    if (!item.total_dwt || !item.items_dwt || !item.materials_dwt) {
                        this.canProceed = false;
                    }
                });
                //non save, just to verifiy the items can proceed
            },
            deep: true
        },
    },
    mounted: function () {
        this.getData();

        this.$barcodeScanner.init(rawBarcode => {
            this.$barcodeScanner.parseBarcode(rawBarcode, barcode => {
                this.target_barcode = null;
                this.$forceUpdate();
                this.$nextTick(() => {
                    this.target_barcode = barcode;
                });
            });
        });
    }
};
</script>

<style scoped>
.min-card-height {
    min-height: 150px;
}

.sub-add-accordion a:after {
    font-family: 'Font Awesome 5 Pro';
    content: "\f056";
    float: right;
    color: #888;
}

.sub-add-accordion a.collapsed:after {
    content: "\f055";
}

.next-step {
    animation: nextStep 1s 1s 1 forwards;
}

@keyframes nextStep {
    0% {
        box-shadow: 0 0 0 0 #1ad227;
    }
    33% {
        box-shadow: 0 0 0 4px #1ad227;
    }
    66% {
        box-shadow: 0 0 0 0 #1ad227;
    }
    100% {
        box-shadow: 0 0 0 2px #1ad227;
    }
}

.next-package {
    animation: nextPackage 1s 1s 1 forwards;
}

@keyframes nextPackage {
    0% {
        box-shadow: 0 0 0 0 #1ad227;
    }
    30% {
        box-shadow: 0 0 0 4px #1ad227;
    }
    60% {
        box-shadow: 0 0 0 0 #1ad227;
    }
    80% {
        box-shadow: 0 0 0 2px #1ad227;
    }
    100% {
        box-shadow: 0 0 0 0px #1ad227;
    }
}

.delete-photo-icon {
    position: absolute;
    top: 0;
    left: 5px;
    z-index: 1;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.multiselect__option {
    padding: 8px !important;
    line-height: 10px !important;
    min-height: 20px !important;
    font-size: 0.8em;
}

.multiselect__tags, .multiselect__select {
    padding: 0;
    margin-left: 2px;
    min-height: 33px !important;
    height: 33px !important;
    max-height: 33px !important;
}

.multiselect__placeholder {
    margin-left: 4px;
}

.multiselect__single {
    margin-top: 3px;
    font: 400 10px Arial !important;
    padding: 0 !important;
}

.multiselect__tags {
    padding-top: 4px;
    padding-right: 30px !important;
}

.multiselect {
    min-height: 33px !important;
    height: 33px !important;
    max-height: 33px !important;
}

.multiselect__option--group.multiselect__option--disabled {
    font-weight: bold;
    color: black !important;
}
</style>